/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import { array } from 'prop-types';
import { useQuery } from '@apollo/client';
import mbpLogger from 'mbp-logger';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { getBrandName } from '../../../../../../state/ducks/App/ducks/Brand/Brand-Selectors';
import findContentByUID from '../../../../../gql/queries/findContentByUID';
import { GRAPHQL_ENV } from '../../../../../gql';
import SiteWideBannerMultiOffer from './SiteWideBannerMultiOffer';
import { getPageType } from '../../../../../../state/ducks/App/App-Selectors';
import useIsPlaPage from '../../../../../helpers/hooks/useIsPlaPage';
import useIsDisplayChannel from '../../../../../helpers/hooks/useIsDisplayChannel';
import isProductListingAd from '../../../../../helpers/isProductListingAd';
import useUTMRules from '../../../../../helpers/DynamicRules/hooks/useUTMRules';

const SiteWideMultiOfferBannerContainer = ({ bannerData }) => {
    const pageType = useSelector(getPageType);
    const location = useLocation();
    const currentUrl = location?.pathname;
    const isPlaPage = useIsPlaPage();
    const isDisplayChannel = useIsDisplayChannel();
    const brand = useSelector(getBrandName) || '1800flowers';
    const isPlaPagesEnabled =        isPlaPage || isDisplayChannel || isProductListingAd(location?.search);

    const { _content_type_uid, uid } = bannerData?.reference?.[0] || {};
    if (!uid) return null;

    const SITEWIDEMULTIOFFER_QUERY = findContentByUID({
        brand,
        uid,
        contentType: _content_type_uid,
        includeRefs: ['multibanner_options.inclusions_exclusions'],
    });

    const { data, error, loading } = useQuery(SITEWIDEMULTIOFFER_QUERY);

    if (loading) return null;

    if (error) {
        mbpLogger.logWarning({
            appName: process.env.npm_package_name,
            query: SITEWIDEMULTIOFFER_QUERY,
            component: 'SiteWideMultiOffer.js',
            message: 'Error in query',
            env: GRAPHQL_ENV,
        });
        return null;
    }

    if (!data.findContentByUID || data.findContentByUID?.content?.entry?.banner?.length === 0) {
        mbpLogger.logWarning({
            appName: process.env.npm_package_name,
            query: SITEWIDEMULTIOFFER_QUERY,
            component: 'SiteWideMultiOffer.js',
            message: 'No data returned for query',
            env: GRAPHQL_ENV,
        });
        return null;
    }

    const multiBannerData = data.findContentByUID?.content?.entry;
    const inclusionExclusionData = multiBannerData?.multibanner_options?.inclusions_exclusions?.[0] || {};
    if (!inclusionExclusionData || Object.keys(inclusionExclusionData).length === 0 || !inclusionExclusionData.type) {
        return <SiteWideBannerMultiOffer multiBannerData={multiBannerData} />;
    }

    if (!inclusionExclusionData?.type || !pageType) return null;

    const configuredPageTypes = Array.isArray(inclusionExclusionData.page_types)
        ? inclusionExclusionData.page_types.map((p) => p.toLowerCase())
        : [];

    const pageTypeMatches = configuredPageTypes.includes(pageType?.toLowerCase())
        || (configuredPageTypes.includes('plas') && isPlaPagesEnabled);

    const normalize = (url) => {
        if (!url) return '';
        const clean = url?.trim()?.replace?.(/,+$/, '');
        return clean === '/' ? '/' : clean?.replace?.(/\/+$/, '');
    };

    const isUrlMatched = () => {
        if (!Array.isArray(inclusionExclusionData?.urls) || !currentUrl) return false;
        const normalizedCurrent = normalize(currentUrl);
        return inclusionExclusionData.urls.some((group) => {
            const includeAll = !!group.include_all;
            const urlList = Array.isArray(group.url_group)
                ? group.url_group.flatMap((u) => u
                    .split(',')
                    .map((part) => normalize(part))
                    .filter(Boolean),
                )
                : [];
            return urlList.some((url) => (includeAll ? normalizedCurrent.includes(url) : normalizedCurrent === url),
            );
        });
    };

    const type = inclusionExclusionData?.type?.toLowerCase();

    let shouldShowBanner = false;
    if (type === 'exclusion') {
        shouldShowBanner = !(pageTypeMatches || isUrlMatched());
    } else if (type === 'inclusion') {
        shouldShowBanner = pageTypeMatches || isUrlMatched();
    }
    const utmPassed = useUTMRules(multiBannerData?.multibanner_options);

    if (!utmPassed || !shouldShowBanner) {
        return <></>;
    }

    return <SiteWideBannerMultiOffer multiBannerData={multiBannerData} />;
};

SiteWideMultiOfferBannerContainer.propTypes = {
    bannerData: array.isRequired,
};

export default SiteWideMultiOfferBannerContainer;
